import { FC, ReactElement, ReactNode } from 'react'

import { Box, BoxProps, Grid } from '@mantine/core'

import { FeatureCardContext } from './context'
import FeatureCardContent from './elements/_Content'
import FeatureCardHeading from './elements/_Heading'
import FeatureCardIcon from './elements/_Icon'
import FeatureCardInner from './elements/_Inner'
import FeatureCardLeftContent from './elements/_LeftContent'
import FeatureCardRightContent from './elements/_RightContent'
import FeatureCardSectionName from './elements/_SectionName'
import FeatureCardViewAll from './elements/_ViewAll'
import useStyles from './index.styles'
import { HorizontalLayout } from './layout/Horizontal'
import { VerticalLayout } from './layout/Vertical'
import { VerticalCenterLayout } from './layout/VerticalCenterLayout'
import { BaseProps, FeatureCardLayout } from './types'

type VerticalHorizontalProps = BaseProps & {
  layout?:
    | FeatureCardLayout.Vertical
    | FeatureCardLayout.Horizontal
    | FeatureCardLayout.VerticalCenter
  children: ReactNode
} & BoxProps

type TwoColumnsProps = BaseProps & {
  layout: FeatureCardLayout.TwoColumns
  children: never
} & BoxProps

export type FeatureCardProps = VerticalHorizontalProps | TwoColumnsProps

const FeatureCardBase: FC<FeatureCardProps> = ({
  layout = 'vertical',
  color,
  children,
  styles,
  className,
  classNames,
  url,
  ...rest
}) => {
  const { classes, cx } = useStyles(
    { color },
    {
      name: 'FeatureCard',
      styles,
      classNames,
    },
  )
  const slots: {
    sectionName: ReactNode
    icon: ReactNode
    heading: ReactNode
    viewAll: ReactNode
    content: ReactNode
    left: ReactNode
    right: ReactNode
    inner: ReactNode
  } = {
    sectionName: null,
    icon: null,
    heading: null,
    viewAll: null,
    content: null,
    left: null,
    right: null,
    inner: null,
  }

  const childArray = (Array.isArray(children)
    ? children
    : [children]) as ReactElement[]

  childArray.forEach((child) => {
    if (!child?.type) return

    const name = (child.type as any).displayName

    switch (name) {
      case 'FeatureCardSectionName':
        slots.sectionName = child
        break
      case 'FeatureCardHeading':
        slots.heading = child
        break
      case 'FeatureCardIcon':
        slots.icon = child
        break
      case 'FeatureCardViewAll':
        slots.viewAll = child
        break
      case 'FeatureCardContent':
        slots.content = child
        break
      case 'FeatureCardLeftContent':
        slots.left = child
        break
      case 'FeatureCardRightContent':
        slots.right = child
        break
      case 'FeatureCardInner':
        slots.inner = child
        break
      default:
        break
    }
  })
  return (
    <FeatureCardContext.Provider
      value={{
        color,
        url,
        ...slots,
      }}
    >
      <Box className={cx(classes.root, className)} {...rest}>
        {layout === 'two-columns' ? (
          <Grid>{children}</Grid>
        ) : layout === FeatureCardLayout.Horizontal ? (
          <HorizontalLayout />
        ) : layout === FeatureCardLayout.Vertical ? (
          <VerticalLayout />
        ) : layout === FeatureCardLayout.VerticalCenter ? (
          <VerticalCenterLayout
            classNames={{
              inner: classes.inner,
              header: classes.header,
            }}
          />
        ) : (
          children
        )}
      </Box>
    </FeatureCardContext.Provider>
  )
}

export const FeatureCard = FeatureCardBase as FC<FeatureCardProps> & {
  SectionName: typeof FeatureCardSectionName
  Heading: typeof FeatureCardHeading
  Icon: typeof FeatureCardIcon
  ViewAll: typeof FeatureCardViewAll
  Content: typeof FeatureCardContent
  LeftContent: typeof FeatureCardLeftContent
  RightContent: typeof FeatureCardRightContent
  Inner: typeof FeatureCardInner
}

FeatureCard.SectionName = FeatureCardSectionName
FeatureCard.Heading = FeatureCardHeading
FeatureCard.Icon = FeatureCardIcon
FeatureCard.ViewAll = FeatureCardViewAll
FeatureCard.Content = FeatureCardContent
FeatureCard.LeftContent = FeatureCardLeftContent
FeatureCard.RightContent = FeatureCardRightContent
FeatureCard.Inner = FeatureCardInner

export default FeatureCard
